/**
 * Brand asset library accessors (facelift program, 2026-07-30).
 *
 * `config.brand.assets` carries the brand's signature layer — the motif
 * kit (star charts for Stellar, contour lines for Atlas, …) and the
 * brand image library — from `cms.brand_assets` via the engine mapper.
 *
 * Contract: every consumer must render NOTHING when a lookup returns
 * null. Brands without assets (and older cached configs, which simply
 * lack the key) get exactly the pre-facelift page. One bundle, many
 * tenants — the brand look lives in data, never in component branches.
 */

import { useTenantConfig } from './useTenantConfig.js';
import type { BrandAssets } from '@aamp/shared-types';

function useBrandAssets(): BrandAssets | null {
  const tenant = useTenantConfig();
  if (tenant.kind !== 'live' && tenant.kind !== 'paused') return null;
  return tenant.config.brand.assets ?? null;
}

/**
 * URL of a motif in its brand-default colourway, or null.
 * Roles: 'hero-backdrop' | 'tile' | 'edge' | 'empty-state' | 'sticker'.
 */
export function useBrandMotif(role: string): string | null {
  const assets = useBrandAssets();
  return assets?.motifs[role]?.['default'] ?? null;
}

/**
 * URL of the first brand image for a role at the given aspect ratio
 * (production-id order — p01 first, deliberately deterministic), or null.
 * Roles: 'hero' | 'detail' | 'fans' | 'sky-to-stadium' | 'abstract'.
 */
export function useBrandImage(role: string, ratio: string): string | null {
  const assets = useBrandAssets();
  const hit = assets?.images[role]?.find((i) => i.ratio === ratio);
  return hit?.url ?? null;
}

/**
 * URL of a product icon for a role, or null (platform inline glyph is
 * the fallback — see BrandIcon). Roles: nav-matches, nav-news,
 * nav-stories, play-large, play-inline, video-chip, info, close.
 */
export function useBrandIcon(role: string): string | null {
  const assets = useBrandAssets();
  return assets?.icons?.[role] ?? null;
}
